<template>
    <div
        class="ds-multiselect-group p-2 [&:not(:last-child)]:border-b [&:not(:last-child)]:border-ds-primary-200 [&:not(:last-child)]:pb-3"
    >
        <div class="ds-multiselect-group-label px-3 py-1">
            <p class="text-content-md font-semibold text-content-secondary">{{ label }}</p>
        </div>
        <DsMultiSelectOption
            :disabled="!totalOptions"
            :isSelected="groupSelectState === SelectAllState.Checked"
        >
            <template #leading>
                <DsCheckbox
                    :id="`group-${label}-select-all`"
                    :disabled="!totalOptions"
                    :icon="groupSelectState === SelectAllState.Indeterminate ? 'fa-solid fa-minus' : 'fa-solid fa-check'"
                    label="Select all"
                    :model-value="
                        groupSelectState === SelectAllState.Checked || groupSelectState === SelectAllState.Indeterminate
                    "
                    :name="`Select all ${label}`"
                    :value="'all'"
                    @update:model-value="handleGroupSelectAll"
                />
            </template>
        </DsMultiSelectOption>
        <div class="ds-multiselect-group-items">
            <slot />
        </div>
    </div>
</template>

<script setup lang="ts" generic="T">
import DsCheckbox from '@/components/design-system/form/input/DsCheckbox.vue';
import DsMultiSelectOption from '@/components/design-system/multiselect/DsMultiSelectOption.vue';
import { SelectAllState, type DsMultiSelectOptionBase } from '@/components/design-system/multiselect/multi-select.types';
import { computed } from 'vue';

const props = defineProps<{
    label: string;
    items: (T & DsMultiSelectOptionBase)[];
    selectedOptions: T[];
}>();

const emit = defineEmits<{
    (e: 'groupSelectAll', checked: boolean): void;
}>();

const totalOptions = computed(() => {
    return props.items.filter(opt => !opt.disabled).length;
});

const selectedCount = computed(() => {
    return props.items.filter(item => {
        if (item.disabled) return false; // Don't count disabled items
        const { value: __, label: ___, ...rest } = item;
        return props.selectedOptions.some(selectedItem =>
            Object.entries(rest).every(([key, val]) => selectedItem[key as keyof T] === val),
        );
    }).length;
});

const groupSelectState = computed(() => {
    if (selectedCount.value === 0) return SelectAllState.Unchecked;
    if (selectedCount.value === totalOptions.value) return SelectAllState.Checked;
    return SelectAllState.Indeterminate;
});

const handleGroupSelectAll = () => {
    emit('groupSelectAll', groupSelectState.value === SelectAllState.Unchecked);
};
</script>
